import { useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import toast from 'react-hot-toast';
import { harvestService } from '../../services/harvestService';
import { clientService } from '../../services/clientService';
import { parseAddress } from '../../utils/addressParser';
import type { Client } from '@shared/types';

interface HarvestClient {
  id: number;
  name: string;
  is_active: boolean;
  address: string;
  currency: string;
  // Harvest may not have all our fields, so we'll map what's available
}

interface HarvestClientImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

export function HarvestClientImportModal({ isOpen, onClose, onImportComplete }: HarvestClientImportModalProps) {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<HarvestClient[]>([]);
  const [selectedClients, setSelectedClients] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  const loadClients = async () => {
    setLoading(true);
    const toastId = toast.loading('Loading clients from Harvest...');
    
    try {
      const harvestClients = await harvestService.fetchHarvestClients();
      setClients(harvestClients);
      toast.success(`Loaded ${harvestClients.length} clients`, { id: toastId });
    } catch (error) {
      console.error('Failed to load clients:', error);
      toast.error('Failed to load Harvest clients', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleClient = (clientId: number) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const toggleAll = () => {
    if (selectedClients.size === filteredClients.length) {
      setSelectedClients(new Set());
    } else {
      setSelectedClients(new Set(filteredClients.map(c => c.id)));
    }
  };

  const mapHarvestClientToCLM = (harvestClient: HarvestClient): Partial<Client> => {
    // Use intelligent address parser
    const parsedAddress = parseAddress(harvestClient.address || '');
    
    return {
      name: harvestClient.name,
      addressLine1: parsedAddress.addressLine1,
      addressLine2: parsedAddress.addressLine2,
      city: parsedAddress.city,
      state: parsedAddress.state,
      zip: parsedAddress.zip,
      industry: '',
      website: '',
      status: harvestClient.is_active ? 'active' : 'inactive',
      notes: `Imported from Harvest on ${new Date().toLocaleDateString()}`,
      // Store external system info
      externalSource: 'harvest',
      externalId: String(harvestClient.id),
      externalData: {
        harvestId: harvestClient.id,
        currency: harvestClient.currency,
        importedAt: new Date().toISOString(),
        originalAddress: harvestClient.address,
      },
    };
  };

  const handleImport = async () => {
    if (selectedClients.size === 0) {
      toast.error('Please select at least one client');
      return;
    }

    setImporting(true);
    const toastId = toast.loading(`Importing ${selectedClients.size} client(s)...`);
    
    try {
      const clientsToImport = clients.filter(c => selectedClients.has(c.id));
      let successCount = 0;
      let skipCount = 0;
      let errorCount = 0;

      for (const harvestClient of clientsToImport) {
        try {
          // Check if client already exists by external ID (more reliable than name)
          const existing = await clientService.getClientByExternalId('harvest', String(harvestClient.id));
          
          if (existing) {
            console.log(`Skipping ${harvestClient.name} - already imported (ID: ${harvestClient.id})`);
            skipCount++;
            continue;
          }

          // Import new client
          const clmClient = mapHarvestClientToCLM(harvestClient);
          await clientService.createClient(clmClient);
          console.log(`✅ Imported: ${harvestClient.name}`);
          successCount++;
        } catch (error) {
          console.error(`❌ Failed to import ${harvestClient.name}:`, error);
          errorCount++;
        }
      }

      const message = [
        successCount > 0 && `${successCount} imported`,
        skipCount > 0 && `${skipCount} skipped (already exist)`,
        errorCount > 0 && `${errorCount} failed`,
      ].filter(Boolean).join(', ');

      if (successCount > 0) {
        toast.success(`Clients: ${message}`, { id: toastId });
        onImportComplete();
        onClose();
      } else {
        toast.error(`Import complete: ${message}`, { id: toastId });
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error('Failed to import clients', { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">
                  Import Clients from Harvest
                </Dialog.Title>

                {/* Search */}
                <div className="mb-4">
                  <input
                    type="text"
                    placeholder="Search clients..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>

                {/* Select All */}
                {filteredClients.length > 0 && (
                  <div className="mb-2 flex items-center px-4 py-2 bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={selectedClients.size === filteredClients.length && filteredClients.length > 0}
                      onChange={toggleAll}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label className="ml-3 text-sm font-medium text-gray-700">
                      Select All ({filteredClients.length} clients)
                    </label>
                  </div>
                )}

                {/* Client List */}
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                  {loading ? (
                    <div className="p-8 text-center text-gray-500">
                      Loading clients...
                    </div>
                  ) : filteredClients.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      No clients found
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-200">
                      {filteredClients.map((client) => (
                        <div
                          key={client.id}
                          className="flex items-center px-4 py-3 hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleClient(client.id)}
                        >
                          <input
                            type="checkbox"
                            checked={selectedClients.has(client.id)}
                            onChange={() => toggleClient(client.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <div className="ml-3 flex-1">
                            <div className="text-sm font-medium text-gray-900">
                              {client.name}
                            </div>
                            {client.address && (
                              <div className="text-sm text-gray-500">
                                {client.address.split('\n')[0]}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            {client.currency && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                {client.currency}
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              client.is_active
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {client.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="mt-6 flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    {selectedClients.size} client(s) selected
                  </div>
                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={importing}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleImport}
                      disabled={selectedClients.size === 0 || importing}
                      className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {importing ? 'Importing...' : `Import ${selectedClients.size || ''} Client(s)`}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

